import type { QueryKey, QueryMeta, QueryState } from '@tanstack/react-query';
import { hashKey } from '@tanstack/react-query';

import { getBarberByIdOrRouteQuery } from '@/api/booking/getBarberByIdOrRouteQuery';
import { getProfessionalsQuery } from '@/api/booking/getProfessionalsQuery';
import type { FetchProfessionalSort } from '@/api/booking/getProfessionalsQuery/types';
import { homeQuery } from '@/api/home/homeQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';

export interface SanitizedQuery {
  queryKey: QueryKey;
  queryHash: string;
  state: Partial<QueryState> & { status: 'success'; dataUpdatedAt: number };
  meta: QueryMeta;
}

export interface SanitizedHydration {
  queries: SanitizedQuery[];
  dropped: number;
}

export interface WidgetEntryPayload {
  setup: { brand: string; shop?: string; barber?: string };
  home?: unknown;
  shop?: unknown;
  professionals?: unknown;
  professional?: unknown;
  params: {
    shopInclude: string[];
    professionalsInclude: string[];
    professionalsSort: FetchProfessionalSort[];
  };
  fetchedAt: number;
}

type WithBarber = { barber: { id: string; order?: unknown } };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isOptionalString = (value: unknown): boolean =>
  value === undefined || typeof value === 'string';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isSort = (value: unknown): value is FetchProfessionalSort[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      isRecord(item) &&
      typeof item.name === 'string' &&
      (item.type === 'asc' || item.type === 'desc'),
  );

const hasBarberId = (value: unknown): value is WithBarber =>
  isRecord(value) &&
  isRecord(value.barber) &&
  typeof value.barber.id === 'string';

const orderOf = (item: WithBarber): number =>
  typeof item.barber.order === 'number' ? item.barber.order : 0;

const sameKey = (a: QueryKey, b: QueryKey): boolean =>
  hashKey(a) === hashKey(b);

export const isWidgetEntryPayload = (
  value: unknown,
): value is WidgetEntryPayload =>
  isRecord(value) &&
  isRecord(value.setup) &&
  typeof value.setup.brand === 'string' &&
  isOptionalString(value.setup.shop) &&
  isOptionalString(value.setup.barber) &&
  isRecord(value.params) &&
  isStringArray(value.params.shopInclude) &&
  isStringArray(value.params.professionalsInclude) &&
  isSort(value.params.professionalsSort) &&
  typeof value.fetchedAt === 'number' &&
  Number.isFinite(value.fetchedAt);

const seed = (
  queryKey: QueryKey,
  data: unknown,
  updatedAt: number,
): SanitizedQuery => ({
  queryKey,
  queryHash: hashKey(queryKey),
  state: {
    data,
    dataUpdateCount: 1,
    dataUpdatedAt: updatedAt,
    error: null,
    errorUpdateCount: 0,
    errorUpdatedAt: 0,
    fetchFailureCount: 0,
    fetchFailureReason: null,
    fetchMeta: null,
    isInvalidated: false,
    status: 'success',
    fetchStatus: 'idle',
  },
  meta: { persist: false },
});

// The framing page is untrusted, so keys come from each query's factory and
// an entity is seeded only when its params match the query's defaults.
export const buildWidgetEntryQueries = (
  payload: WidgetEntryPayload,
  now: number = Date.now(),
): SanitizedHydration => {
  const { setup, params } = payload;
  const updatedAt = Math.min(payload.fetchedAt, now);
  const queries: SanitizedQuery[] = [];
  const offered = [
    payload.home,
    payload.shop,
    payload.professionals,
    payload.professional,
  ].filter((value) => value !== undefined).length;

  const { home } = payload;
  if (
    isRecord(home) &&
    isRecord(home.brand) &&
    typeof home.brand.id === 'string'
  ) {
    queries.push(
      seed(homeQuery({ brandId: setup.brand }).queryKey, home, updatedAt),
    );
  }

  const { shop } = payload;
  const shopId =
    isRecord(shop) && typeof shop.id === 'string' ? shop.id : undefined;

  if (shopId && setup.shop) {
    const queryKey = shopDetailQuery(setup.shop, {
      include: params.shopInclude,
    }).queryKey;
    if (sameKey(queryKey, shopDetailQuery(setup.shop).queryKey)) {
      queries.push(seed(queryKey, shop, updatedAt));
    }
  }

  const { professionals } = payload;
  if (
    shopId &&
    Array.isArray(professionals) &&
    professionals.every(hasBarberId)
  ) {
    const queryKey = getProfessionalsQuery(
      shopId,
      params.professionalsInclude,
      true,
      params.professionalsSort,
    ).queryKey;
    if (sameKey(queryKey, getProfessionalsQuery(shopId).queryKey)) {
      const sorted = [...professionals].sort((a, b) => orderOf(a) - orderOf(b));
      queries.push(seed(queryKey, sorted, updatedAt));
    }
  }

  const { professional } = payload;
  if (shopId && setup.barber && hasBarberId(professional)) {
    const queryKey = getBarberByIdOrRouteQuery(
      shopId,
      setup.barber,
      params.professionalsInclude,
    ).queryKey;
    if (
      sameKey(
        queryKey,
        getBarberByIdOrRouteQuery(shopId, setup.barber).queryKey,
      )
    ) {
      queries.push(seed(queryKey, professional, updatedAt));
    }
  }

  return { queries, dropped: offered - queries.length };
};
